import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BarChart3,
  Users,
  Eye,
  MousePointer,
  Smartphone,
  Monitor,
  TrendingUp,
  Calendar,
  Download,
  RefreshCw,
  Activity
} from "lucide-react";
import { getAnalyticsSummary, exportAnalyticsData } from "@/services/analytics";

const BetaAnalyticsDashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAnalytics = () => {
    setLoading(true);
    const data = getAnalyticsSummary();
    setAnalytics(data);
    setLoading(false);
  };

  useEffect(() => {
    loadAnalytics();
    // Refresh every 30 seconds
    const interval = setInterval(loadAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleExport = () => {
    const data = exportAnalyticsData();
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `myhairchair-analytics-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !analytics) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-amber-400" />
            Beta Analytics Dashboard
          </h2>
          <p className="text-stone-400 mt-1">
            Proof of concept data for investors
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={loadAnalytics}
            className="border-stone-600 text-stone-300"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button
            onClick={handleExport}
            className="bg-amber-500 hover:bg-amber-600 text-black"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/20 to-cyan-500/20 border-blue-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-400 text-sm">Total Sessions</p>
                <p className="text-3xl font-bold text-white">{analytics.overview.totalSessions}</p>
              </div>
              <Users className="w-10 h-10 text-blue-400 opacity-50" />
            </div>
            <p className="text-xs text-blue-300 mt-2">
              {analytics.today.sessions} today
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/20 to-yellow-500/20 border-amber-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-400 text-sm">Page Views</p>
                <p className="text-3xl font-bold text-white">{analytics.overview.pageViews}</p>
              </div>
              <Eye className="w-10 h-10 text-amber-400 opacity-50" />
            </div>
            <p className="text-xs text-amber-300 mt-2">
              {analytics.today.pageViews} today
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-green-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-400 text-sm">Registrations</p>
                <p className="text-3xl font-bold text-white">{analytics.overview.registrations}</p>
              </div>
              <TrendingUp className="w-10 h-10 text-green-400 opacity-50" />
            </div>
            <p className="text-xs text-green-300 mt-2">
              Conversion goal
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/20 to-pink-500/20 border-purple-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-400 text-sm">Bookings</p>
                <p className="text-3xl font-bold text-white">{analytics.overview.bookings}</p>
              </div>
              <Calendar className="w-10 h-10 text-purple-400 opacity-50" />
            </div>
            <p className="text-xs text-purple-300 mt-2">
              Demo + Real
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Device Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="bg-stone-900/50 border-stone-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-amber-400" />
              Device Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Smartphone className="w-5 h-5 text-blue-400" />
                  <span className="text-stone-300">Mobile</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-stone-700 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full" 
                      style={{ width: `${analytics.devices.mobilePercent}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium">{analytics.devices.mobilePercent}%</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Monitor className="w-5 h-5 text-green-400" />
                  <span className="text-stone-300">Desktop</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-stone-700 rounded-full h-2">
                    <div 
                      className="bg-green-500 h-2 rounded-full" 
                      style={{ width: `${100 - analytics.devices.mobilePercent}%` }}
                    ></div>
                  </div>
                  <span className="text-white font-medium">{100 - analytics.devices.mobilePercent}%</span>
                </div>
              </div>
            </div>
            <p className="text-xs text-stone-500 mt-4">
              {analytics.devices.mobile} mobile / {analytics.devices.desktop} desktop events
            </p>
          </CardContent>
        </Card>

        <Card className="bg-stone-900/50 border-stone-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Eye className="w-5 h-5 text-amber-400" />
              Popular Pages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics.popularPages.length > 0 ? (
                analytics.popularPages.slice(0, 5).map(([page, count], index) => (
                  <div key={page} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-stone-500 text-sm w-4">{index + 1}.</span>
                      <span className="text-stone-300 text-sm truncate max-w-[150px]">{page}</span>
                    </div>
                    <Badge variant="outline" className="border-stone-600 text-stone-400">
                      {count} views
                    </Badge>
                  </div>
                ))
              ) : (
                <p className="text-stone-500 text-sm">No page data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Feature Usage */}
      <Card className="bg-stone-900/50 border-stone-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-amber-400" />
            Feature Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          {analytics.popularFeatures.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {analytics.popularFeatures.map(([feature, count]) => (
                <div 
                  key={feature} 
                  className="bg-stone-800/50 rounded-lg p-3 text-center"
                >
                  <p className="text-2xl font-bold text-amber-400">{count}</p>
                  <p className="text-stone-400 text-sm capitalize">{feature.replace(/_/g, ' ')}</p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-stone-500 text-center py-8">
              Feature usage data will appear as users interact with the app
            </p>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="bg-stone-900/50 border-stone-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-400" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {analytics.recentEvents.length > 0 ? (
              analytics.recentEvents.map((event, index) => (
                <div 
                  key={index}
                  className="flex items-center justify-between py-2 border-b border-stone-800 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <Badge 
                      className={
                        event.event === 'conversion' ? 'bg-green-500/20 text-green-400' :
                        event.event === 'error' ? 'bg-red-500/20 text-red-400' :
                        'bg-stone-700 text-stone-300'
                      }
                    >
                      {event.event}
                    </Badge>
                    <span className="text-stone-400 text-sm">
                      {event.data?.action || event.data?.page || event.data?.type || 'event'}
                    </span>
                  </div>
                  <span className="text-stone-500 text-xs">
                    {new Date(event.timestamp).toLocaleTimeString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-stone-500 text-center py-8">
                Activity will appear as users use the app
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Investor Summary */}
      <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/10 border-amber-500/30">
        <CardHeader>
          <CardTitle className="text-amber-400 flex items-center gap-2">
            📊 30-Day Beta Summary (Investor Report)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl font-bold text-white">{analytics.overview.totalSessions}</p>
              <p className="text-stone-400 text-sm">Unique Visitors</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{analytics.overview.registrations}</p>
              <p className="text-stone-400 text-sm">Sign Ups</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">{analytics.overview.bookings}</p>
              <p className="text-stone-400 text-sm">Bookings Made</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-white">
                {analytics.overview.totalSessions > 0 
                  ? Math.round((analytics.overview.registrations / analytics.overview.totalSessions) * 100) 
                  : 0}%
              </p>
              <p className="text-stone-400 text-sm">Conversion Rate</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BetaAnalyticsDashboard;
